import { useState } from 'react'
import { Button } from '@/components/ui/button'
import { useInsights, type InsightsRange } from '@/client/modules/dashboard/hooks/useInsights'
import { OverviewCards } from '@/client/modules/dashboard/components/OverviewCards'
import { SearchVolumeChart } from '@/client/modules/dashboard/components/SearchVolumeChart'
import { HourlyHeatmap } from '@/client/modules/dashboard/components/HourlyHeatmap'
import { TopQueriesTable } from '@/client/modules/dashboard/components/TopQueriesTable'
import { ZeroResultsActionable } from '@/client/modules/dashboard/components/ZeroResultsActionable'
import { ShareBars } from '@/client/modules/dashboard/components/ShareBars'
import { TopClickedProducts } from '@/client/modules/dashboard/components/TopClickedProducts'
import { BarChart3, RefreshCw, AlertCircle } from 'lucide-react'

const RANGES: { value: InsightsRange; label: string }[] = [
  { value: '24h', label: 'Last 24 hours' },
  { value: '7d', label: 'Last 7 days' },
  { value: '30d', label: 'Last 30 days' },
]

export function InsightsPage() {
  const [range, setRange] = useState<InsightsRange>('7d')
  const { data, isLoading, error, refetch, isFetching } = useInsights(range)

  const brandData = data?.brandShare.map((b) => ({
    label: b.brand,
    clicks: b.clicks,
    percentage: b.percentage,
  }))
  const categoryData = data?.categoryShare.map((c) => ({
    label: c.category,
    clicks: c.clicks,
    percentage: c.percentage,
  }))

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-semibold">
            <BarChart3 className="h-6 w-6" />
            Insights
          </h1>
          <p className="mt-1 text-muted-foreground">
            What customers want, what you're missing, and where the demand is.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="inline-flex overflow-hidden rounded-md border">
            {RANGES.map((r) => (
              <button
                key={r.value}
                onClick={() => setRange(r.value)}
                className={`px-3 py-1.5 text-sm transition-colors ${
                  range === r.value
                    ? 'bg-primary text-primary-foreground'
                    : 'hover:bg-muted'
                }`}
              >
                {r.label}
              </button>
            ))}
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => refetch()}
            disabled={isFetching}
          >
            <RefreshCw className={`mr-2 h-4 w-4 ${isFetching ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {error && (
        <div className="flex items-center gap-2 rounded-lg bg-destructive/10 p-4 text-destructive">
          <AlertCircle className="h-5 w-5" />
          <span>Failed to load insights. Please try refreshing.</span>
        </div>
      )}

      <OverviewCards overview={data?.overview} isLoading={isLoading} />

      <div className="grid gap-6 lg:grid-cols-3">
        <div className="lg:col-span-2">
          <SearchVolumeChart data={data?.timeSeries} isLoading={isLoading} />
        </div>
        <HourlyHeatmap data={data?.hourlyHeatmap} isLoading={isLoading} />
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <TopQueriesTable queries={data?.topQueries} isLoading={isLoading} />
        <ZeroResultsActionable queries={data?.zeroResults} isLoading={isLoading} />
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        <ShareBars
          title="Brand Demand"
          description="Which brands customers click on"
          kind="brand"
          data={brandData}
          isLoading={isLoading}
        />
        <ShareBars
          title="Category Demand"
          description="Which categories customers click on"
          kind="category"
          data={categoryData}
          isLoading={isLoading}
        />
        <TopClickedProducts
          products={data?.topClickedProducts}
          isLoading={isLoading}
        />
      </div>
    </div>
  )
}
